/**
 * Google Maps Places API Service
 * Uses Google Maps JavaScript API for client-side access (CORS-friendly)
 */

class GoogleMapsPlacesService {
  constructor() {
    this.apiKey = 'AIzaSyB84hq9Mx8l7EWIAKX13QyPTTz6tWBxAhA';
    this.isLoaded = false;
    this.loadPromise = null;
  }

  /**
   * Load Google Maps JavaScript API
   * @returns {Promise<void>}
   */
  loadGoogleMapsAPI() {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    if (window.google && window.google.maps && window.google.maps.places) {
      this.isLoaded = true;
      return Promise.resolve();
    }

    this.loadPromise = new Promise((resolve, reject) => {
      if (!this.apiKey) {
        reject(new Error('Google Maps API key not configured'));
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      // Set up callback
      window.initGoogleMaps = () => {
        this.isLoaded = true;
        delete window.initGoogleMaps;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Get opening hours using Google Maps Places API
   * @param {string} placeId - Google Places ID
   * @returns {Promise<Object>} Opening hours data
   * 
   * Note: Uses isOpen() method instead of deprecated open_now property
   * as per Google's recommendation: https://goo.gle/js-open-now
   */
  async getOpeningHours(placeId) {
    try {
      await this.loadGoogleMapsAPI();

      return new Promise((resolve, reject) => {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );

        service.getDetails(
          {
            placeId: placeId,
            fields: ['opening_hours', 'current_opening_hours', 'name', 'place_id']
          },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              const openingHours = place.opening_hours || place.current_opening_hours;
              
              if (!openingHours) {
                resolve({
                  isOpen: null,
                  weekdayText: [],
                  periods: [],
                  error: 'Opening hours not available'
                });
                return;
              }

              // Use the newer isOpen() method instead of deprecated open_now
              let isCurrentlyOpen = false;
              try {
                if (openingHours && typeof openingHours.isOpen === 'function') {
                  isCurrentlyOpen = openingHours.isOpen();
                } else if (openingHours && openingHours.open_now !== undefined) {
                  // Fallback to open_now if isOpen() is not available
                  isCurrentlyOpen = openingHours.open_now;
                }
              } catch (error) {
                console.warn('Could not determine open status:', error);
                isCurrentlyOpen = false;
              }

              resolve({
                isOpen: isCurrentlyOpen,
                weekdayText: openingHours.weekday_text || [],
                periods: openingHours.periods || [],
                specialHours: openingHours.special_hours || [],
                placeName: place.name
              });
            } else {
              reject(new Error(`Google Places API error: ${status}`));
            }
          }
        );
      });
    } catch (error) {
      console.error('Error fetching opening hours:', error);
      return {
        isOpen: null,
        weekdayText: [],
        periods: [],
        error: error.message
      };
    }
  }

  /**
   * Convert USA 12-hour time format to German 24-hour format
   * @param {string} usaTime - Time in USA format (e.g., "9:00 AM – 6:00 PM")
   * @returns {string} Time in German format (e.g., "9:00 - 18:00")
   */
  convertToGermanTime(usaTime) {
    if (!usaTime || usaTime === 'Closed' || usaTime === 'Geschlossen') {
      return 'Geschlossen';
    }

    // Handle time ranges like "9:00 AM – 6:00 PM"
    const timeRange = usaTime.replace(/–|—/g, '-'); // Replace em dash with regular dash
    
    if (timeRange.includes('-')) {
      const [startTime, endTime] = timeRange.split('-').map(t => t.trim());
      const convertedStart = this.convertSingleTimeToGerman(startTime);
      const convertedEnd = this.convertSingleTimeToGerman(endTime);
      return `${convertedStart} - ${convertedEnd}`;
    } else {
      // Single time or already in German format
      return this.convertSingleTimeToGerman(timeRange);
    }
  }

  /**
   * Convert a single USA time to German 24-hour format
   * @param {string} time - Single time like "9:00 AM" or "6:00 PM"
   * @returns {string} Time in 24-hour format like "09:00" or "18:00"
   */
  convertSingleTimeToGerman(time) {
    if (!time || !time.includes(':')) return time;

    const timeStr = time.trim().toUpperCase();
    
    // Check if it's already in 24-hour format (no AM/PM)
    if (!timeStr.includes('AM') && !timeStr.includes('PM')) {
      // Already in 24-hour format, just ensure proper formatting
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours, 10);
      return `${hour.toString().padStart(2, '0')}:${minutes}`;
    }

    // Parse 12-hour format
    const isAM = timeStr.includes('AM');
    const isPM = timeStr.includes('PM');
    
    if (!isAM && !isPM) return time; // No AM/PM indicator, return as is

    const timePart = timeStr.replace(/AM|PM/g, '').trim();
    const [hours, minutes] = timePart.split(':');
    let hour = parseInt(hours, 10);

    if (isPM && hour !== 12) {
      hour += 12;
    } else if (isAM && hour === 12) {
      hour = 0;
    }

    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  }

  /**
   * Format opening hours for display
   * @param {Array} weekdayText - Array of formatted weekday strings from Google Places
   * @returns {Object} Formatted hours object
   */
  formatOpeningHours(weekdayText) {
    if (!weekdayText || weekdayText.length === 0) {
      return {
        monday: 'Geschlossen',
        tuesday: 'Geschlossen',
        wednesday: 'Geschlossen',
        thursday: 'Geschlossen',
        friday: 'Geschlossen',
        saturday: 'Geschlossen',
        sunday: 'Geschlossen',
        formatted: 'Mo-Fr: 9:00 - 18:00\nSa: 10:00 - 16:00\nSo: Geschlossen'
      };
    }

    const dayMapping = {
      'Monday': 'monday',
      'Tuesday': 'tuesday', 
      'Wednesday': 'wednesday',
      'Thursday': 'thursday',
      'Friday': 'friday',
      'Saturday': 'saturday',
      'Sunday': 'sunday',
      'Montag': 'monday',
      'Dienstag': 'tuesday',
      'Mittwoch': 'wednesday',
      'Donnerstag': 'thursday',
      'Freitag': 'friday',
      'Samstag': 'saturday',
      'Sonntag': 'sunday'
    };

    const hours = {};
    let formattedHours = '';

    weekdayText.forEach(dayString => {
      // Parse day and hours from string like "Monday: 9:00 AM – 6:00 PM"
      const colonIndex = dayString.indexOf(':');
      if (colonIndex === -1) return;

      const dayPart = dayString.substring(0, colonIndex).trim();
      const hoursPart = dayString.substring(colonIndex + 1).trim();

      // Find matching day key
      const dayKey = Object.keys(dayMapping).find(key => 
        dayPart.toLowerCase().includes(key.toLowerCase())
      );

      if (dayKey) {
        const mappedDay = dayMapping[dayKey];
        // Convert USA time format to German 24-hour format
        const germanTime = this.convertToGermanTime(hoursPart);
        hours[mappedDay] = germanTime;
      }
    });

    // Create formatted string for display
    if (hours.monday && hours.friday && hours.monday === hours.friday && 
        hours.tuesday === hours.monday && hours.wednesday === hours.monday && 
        hours.thursday === hours.monday && hours.monday !== 'Geschlossen') {
      // Mon-Fri same hours
      formattedHours += `Mo-Fr: ${hours.monday}`;
      if (hours.saturday && hours.saturday !== 'Geschlossen') {
        formattedHours += `\nSa: ${hours.saturday}`;
      }
      if (hours.sunday && hours.sunday !== 'Geschlossen') {
        formattedHours += `\nSo: ${hours.sunday}`;
      } else {
        formattedHours += `\nSo: Geschlossen`;
      }
    } else {
      // Different hours per day
      const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
      const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      
      dayKeys.forEach((key, index) => {
        if (hours[key]) {
          if (formattedHours) formattedHours += '\n';
          formattedHours += `${dayNames[index]}: ${hours[key]}`;
        }
      });
    }

    return {
      ...hours,
      formatted: formattedHours || 'Mo-Fr: 09:00 - 18:00\nSa: 10:00 - 16:00\nSo: Geschlossen'
    };
  }

  /**
   * Convert 24-hour time to 12-hour format for display
   * @param {string} time24 - Time in 24-hour format (e.g., "09:00")
   * @returns {string} Time in 12-hour format (e.g., "9:00 AM")
   */
  convertTo12Hour(time24) {
    if (!time24 || !time24.includes(':')) return time24;

    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${displayHour}:${minutes} ${ampm}`;
  }

  /**
   * Get current business status text
   * @param {boolean} isOpen - Whether the business is currently open
   * @returns {string} Status text in German
   */
  getStatusText(isOpen) {
    if (isOpen === null) return '';
    return isOpen ? 'Jetzt geöffnet' : 'Geschlossen';
  }

  /**
   * Get status badge color
   * @param {boolean} isOpen - Whether the business is currently open
   * @returns {string} CSS color value
   */
  getStatusColor(isOpen) {
    if (isOpen === null) return '#6c757d';
    return isOpen ? '#28a745' : '#dc3545';
  }
}

// Export singleton instance
const googleMapsPlacesServiceInstance = new GoogleMapsPlacesService();
export default googleMapsPlacesServiceInstance;